import styled from '@emotion/styled'
import { createFileRoute } from '@tanstack/react-router'
import type { ReactElement } from 'react'

import { Divider, Tab, Tabbar, useTranslate, Badge } from '@orus.eu/pharaoh'
import { spacing } from '@orus.eu/pharaoh/src/foundations/spacing-tokens'
import { Header as HeaderPattern } from '@orus.eu/pharaoh/src/patterns/header'
import { anyOfPermissions } from '@orus.eu/right-access-management'

import { ContractsDataGridPage } from '../../../components/pages/backoffice/contracts-data-grid-page.tsx'
import { EndorsementsDataGridPage } from '../../../components/pages/backoffice/endorsements-data-grid-page.tsx'
import { HeaderContainer, PageContainer } from '../../../components/templates/data-grid-page-styles.tsx'
import { PermissionChecker } from '../../../components/templates/permission-checker.tsx'
import { usePermissions } from '../../../lib/use-permissions.ts'

const tabContracts = 'contracts'
const tabEndorsements = 'endorsements'
const tabUnpaid = 'unpaid'

export const Route = createFileRoute('/bo/contracts/')({
  component: ContractsPageComponent,
})

function ContractsPageComponent(): ReactElement {
  const { tab_id = tabContracts } = Route.useSearch()
  const translate = useTranslate()
  const { organization } = usePermissions()
  const isPendingOrganization = organization?.status === 'pending'

  return (
    <PermissionChecker requiredPermissions={[anyOfPermissions('contract.read', 'contract.read.allowedOnly')]}>
      <PageContainer>
        <HeaderContainer>
          <HeaderPattern
            title={translate('contracts')}
            badge={
              isPendingOrganization ? (
                <Badge variant="mindaro" icon="lock-regular">
                  {translate('badge_broker_early_access')}
                </Badge>
              ) : undefined
            }
          />
        </HeaderContainer>
        <TabbarContainer>
          <Tabbar size="medium" activeTabId={tab_id}>
            <Tab
              tabId={tabContracts}
              to="/bo/contracts"
              search={{ tab_id: tabContracts }}
              ariaLabel={translate('contracts_signed')}
            >
              {translate('contracts_signed')}
            </Tab>
            <Tab
              tabId={tabEndorsements}
              to="/bo/contracts"
              search={{ tab_id: tabEndorsements }}
              ariaLabel={translate('endorsements')}
            >
              {translate('endorsements')}
            </Tab>
            <Tab
              tabId={tabUnpaid}
              to="/bo/contracts"
              search={{ tab_id: tabUnpaid }}
              ariaLabel={translate('unpaid_tab')}
            >
              {translate('unpaid_tab')}
            </Tab>
          </Tabbar>
        </TabbarContainer>
        <Divider orientation="horizontal" />
        {tab_id === tabContracts ? <ContractsDataGridPage tabId="all" /> : null}
        {tab_id === tabEndorsements ? <EndorsementsDataGridPage /> : null}
        {tab_id === tabUnpaid ? <ContractsDataGridPage tabId="unpaid" /> : null}
      </PageContainer>
    </PermissionChecker>
  )
}

const TabbarContainer = styled.div`
  display: flex;
  align-items: flex-end;
  flex-shrink: 0;
  height: 40px;
  padding: 0 ${spacing[60]};
`
